/**
 * @package FlorenceEGI — EGI — Home Supreme Hero Diptych renderer
 * @author Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version 2.0.0
 * @date 2026-04-22
 * @purpose Island 1 — diptych museale (opera sx + manifesto travertino dx).
 *          Consuma /api/home-supreme/hyper-egis (items[0]). Contents invariati,
 *          layout ridisegnato. Manifesto testi localizzati via data-* del blade.
 *          TODO P0-9: esporre 6 lingue — allo stato inject da data-* su shell.
 */

import { el, clear, formatPrice, lazyImg, buildSrcset, pickVariant } from "./utils";
import type { ImageVariants } from "./utils";
import { buildPlate } from "./plate";

type EgiDto = {
    id: number;
    title: string;
    thumb: string | null;
    image?: ImageVariants;
    price: number | null;
    url: string;
    creator: { id: number; name: string; nick_name: string | null } | null;
    collection: { id: number; name: string | null } | null;
};

type Strings = {
    kicker: string;
    l1: string;
    l1Italic: string;
    l2: string;
    l2Italic: string;
    l3: string;
    lede: string;
    floorLeft: string;
    floorRight: string;
    plaqueId: string;
    priceFallback: string;
};

function readStrings(host: HTMLElement): Strings {
    const ds = host.dataset;
    return {
        kicker:       ds.heroKicker    ?? "I · Manifesto del Rinascimento Digitale",
        l1:           ds.heroL1        ?? "Arte",
        l1Italic:     ds.heroL1Italic  ?? "che",
        l2:           ds.heroL2        ?? "protegge",
        l2Italic:     ds.heroL2Italic  ?? "il",
        l3:           ds.heroL3        ?? "pianeta.",
        lede:         ds.heroLede      ?? "Dal 2024 ogni opera certificata finanzia progetti ambientali verificati. Il 18% del prezzo. Sempre.",
        floorLeft:    ds.heroFloorL    ?? "Sala I · Capolavori",
        floorRight:   ds.heroFloorR    ?? "Firenze — MMXXVI",
        plaqueId:     ds.heroPlaqueId  ?? "OPERA",
        priceFallback: "—",
    };
}

function tick(pos: "tl" | "tr" | "bl" | "br"): HTMLElement {
    return el("span", {
        className: `fe-hero-diptych__tick fe-hero-diptych__tick--${pos}`,
        attrs: { "aria-hidden": "true" },
    });
}

function revealLine(text: string, italic?: string): HTMLElement {
    const wrap = el("span", { className: "fe-line" });
    const inner = el("span");
    inner.appendChild(document.createTextNode(text));
    if (italic) {
        inner.appendChild(document.createTextNode(" "));
        const em = el("em", { className: "fe-italic", text: italic });
        inner.appendChild(em);
    }
    wrap.appendChild(inner);
    return wrap;
}

export function renderHeroDiptych(host: HTMLElement, data: Record<string, unknown>): void {
    clear(host);
    const items = Array.isArray((data as { items?: EgiDto[] }).items)
        ? (data as { items: EgiDto[] }).items
        : [];
    const egi = items[0];
    const s = readStrings(host);

    const wrap = el("div", { className: "fe-hero-diptych" });

    // OPERA — left
    const opera = el("div", { className: "fe-hero-diptych__opera" });
    const media = el("div", { className: "fe-hero-diptych__media" });
    if (egi && (egi.image?.large || egi.thumb)) {
        // Hero occupa 54vw su desktop, 100vw mobile — serve variante grande.
        const bestSrc = pickVariant(egi.image, 1200) ?? egi.image?.large ?? egi.thumb ?? "";
        const srcset = buildSrcset(egi.image);
        const img = lazyImg(bestSrc, egi.title, {
            width: 1200, height: 1500,
            sizes: "(min-width:900px) 54vw, 100vw",
            ...(srcset ? { srcset } : {}),
        });
        img.loading = "eager";
        img.setAttribute("fetchpriority", "high");
        media.appendChild(img);
    } else {
        media.appendChild(buildPlate(egi?.id ? `egi-${egi.id}` : "hero", "nero"));
    }
    opera.appendChild(media);
    opera.append(tick("tl"), tick("tr"), tick("bl"), tick("br"));

    const floor = el("div", { className: "fe-hero-diptych__floor" });
    floor.append(
        el("span", { text: s.floorLeft }),
        el("span", { text: s.floorRight }),
    );
    opera.appendChild(floor);

    // MANIFESTO — right
    const man = el("div", { className: "fe-hero-diptych__manifesto" });
    man.appendChild(el("div", { className: "fe-hero-diptych__grain", attrs: { "aria-hidden": "true" } }));

    const kicker = el("div", { className: "fe-kicker fe-hero-diptych__kicker" });
    kicker.append(el("span", { className: "fe-hair" }), document.createTextNode(s.kicker));
    man.appendChild(kicker);

    const title = el("h2", {
        className: "fe-hero-diptych__title",
        attrs: { "aria-label": `${s.l1} ${s.l1Italic} ${s.l2} ${s.l2Italic} ${s.l3}` },
    });
    title.append(
        revealLine(s.l1, s.l1Italic),
        revealLine(s.l2, s.l2Italic),
        revealLine(s.l3),
    );
    man.appendChild(title);

    man.appendChild(el("p", { className: "fe-hero-diptych__lede", text: s.lede }));
    man.appendChild(el("div", { className: "fe-hero-diptych__spacer" }));

    // PLAQUE
    if (egi) {
        const plaque = el("div", { className: "fe-hero-diptych__plaque" });
        const idCode = (egi.id ? `EGI-${String(egi.id).padStart(4, "0")}` : s.plaqueId);
        const collName = egi.collection?.name ?? "";
        const plaqueIdText = collName ? `${idCode} · ${collName}` : idCode;
        plaque.appendChild(el("div", { className: "fe-hero-diptych__plaque-id", text: plaqueIdText }));

        const row = el("div", { className: "fe-hero-diptych__plaque-row" });
        const info = el("div", { className: "fe-hero-diptych__plaque-info" });
        const plaqueLink = el("a", {
            className: "fe-hero-diptych__plaque-title",
            text: egi.title,
            attrs: { href: egi.url, "aria-label": egi.title },
        });
        info.appendChild(plaqueLink);
        if (egi.creator) {
            info.appendChild(el("div", {
                className: "fe-hero-diptych__plaque-author",
                text: "— " + (egi.creator.nick_name ?? egi.creator.name),
            }));
        }
        row.appendChild(info);
        row.appendChild(el("span", {
            className: "fe-hero-diptych__plaque-price",
            text: formatPrice(egi.price),
        }));
        plaque.appendChild(row);
        man.appendChild(plaque);
    }

    // CHEVRON
    const chev = el("a", {
        className: "fe-hero-diptych__chevron",
        attrs: { href: "#main", "aria-label": "Scroll down" },
    });
    const svgChev = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svgChev.setAttribute("width", "12");
    svgChev.setAttribute("height", "12");
    svgChev.setAttribute("viewBox", "0 0 12 12");
    svgChev.setAttribute("fill", "none");
    svgChev.setAttribute("aria-hidden", "true");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M3 4l3 3 3-3");
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    svgChev.appendChild(path);
    chev.appendChild(svgChev);

    wrap.append(opera, man, chev);
    host.appendChild(wrap);
}
